import { FIELD_NAME_PATTERN } from "../AppSettingsSchema/logicValidation";
import {
  DynamicDefault,
  TConfigItemType,
  TVerificationAppElement,
  TVerificationAppFieldConfigItem,
  VerificationAppDocumentType,
} from "../../models";

export type StepCategory =
  | "default"
  | "beneficialOwnership"
  | "identification"
  | "entityRepresentatives";

export type FieldDefaultEditorKind =
  | "none"
  | "string"
  | "number"
  | "boolean"
  | "falseBoolean"
  | "stringArray"
  | "numberArray"
  | "dynamic";

export const STEP_TYPE_LABELS: Record<string, string> = {
  [VerificationAppDocumentType.UserInformation]: "User Information",
  [VerificationAppDocumentType.Residence]: "Residence",
  [VerificationAppDocumentType.Occupation]: "Occupation",
  [VerificationAppDocumentType.FinancialInformation]: "Financial Information",
  [VerificationAppDocumentType.BeneficialOwnership]: "Beneficial Ownership",
  [VerificationAppDocumentType.EntityInformation]: "Entity Information",
  [VerificationAppDocumentType.DocumentSigning]: "Document Signing",
  [VerificationAppDocumentType.Custom]: "Custom",
  [VerificationAppDocumentType.Identification]: "Identification",
  [VerificationAppDocumentType.EntityRepresentatives]: "Entity Representatives",
};

export const FIELD_TYPE_LABELS: Record<TConfigItemType, string> = {
  [TConfigItemType.TEXT]: "Text",
  [TConfigItemType.NOTE]: "Note",
  [TConfigItemType.URL]: "URL",
  [TConfigItemType.SELECT]: "Select",
  [TConfigItemType.CHECKBOX]: "Checkbox",
  [TConfigItemType.FILE_PICKER]: "File Picker",
  [TConfigItemType.TAX_ID]: "Tax ID",
  [TConfigItemType.PEP]: "PEP",
  [TConfigItemType.SOURCE_OF_ACTIVE]: "Source of Active",
  [TConfigItemType.COUNTRY_SELECT]: "Country Select",
  [TConfigItemType.DATE]: "Date",
};

export const ALL_QUESTIONNAIRE_FIELD_TYPES = Object.keys(
  FIELD_TYPE_LABELS,
) as TConfigItemType[];

export function getStepHeading(step: TVerificationAppElement, index: number): string {
  const typeLabel = STEP_TYPE_LABELS[step.type] ?? step.type;
  const name = step.name?.trim();
  if (!name || name === step.type || name === typeLabel) {
    return `Step ${index + 1}: ${typeLabel}`;
  }
  return `Step ${index + 1}: ${name}`;
}

export function stepHasDistinctName(step: TVerificationAppElement): boolean {
  const name = step.name?.trim();
  if (!name) return false;
  const typeLabel = STEP_TYPE_LABELS[step.type] ?? step.type;
  return name !== step.type && name !== typeLabel;
}

export const STEP_TYPES_WITH_FIELDS: StepCategory[] = [
  "default",
  "beneficialOwnership",
];

export const STEP_TYPE_CATEGORY: Partial<
  Record<VerificationAppDocumentType, StepCategory>
> = {
  [VerificationAppDocumentType.UserInformation]: "default",
  [VerificationAppDocumentType.Residence]: "default",
  [VerificationAppDocumentType.Occupation]: "default",
  [VerificationAppDocumentType.FinancialInformation]: "default",
  [VerificationAppDocumentType.BeneficialOwnership]: "beneficialOwnership",
  [VerificationAppDocumentType.EntityInformation]: "default",
  [VerificationAppDocumentType.DocumentSigning]: "default",
  [VerificationAppDocumentType.Custom]: "default",
  [VerificationAppDocumentType.Identification]: "identification",
  [VerificationAppDocumentType.EntityRepresentatives]: "entityRepresentatives",
};

export const ALL_STEP_TYPES: VerificationAppDocumentType[] = [
  VerificationAppDocumentType.UserInformation,
  VerificationAppDocumentType.Residence,
  VerificationAppDocumentType.Occupation,
  VerificationAppDocumentType.FinancialInformation,
  VerificationAppDocumentType.BeneficialOwnership,
  VerificationAppDocumentType.EntityInformation,
  VerificationAppDocumentType.DocumentSigning,
  VerificationAppDocumentType.Custom,
  VerificationAppDocumentType.Identification,
  VerificationAppDocumentType.EntityRepresentatives,
];

export const moveElement = <T,>(
  arr: T[],
  fromIdx: number,
  direction: "up" | "down",
): T[] => {
  const toIdx = direction === "up" ? fromIdx - 1 : fromIdx + 1;
  if (toIdx < 0 || toIdx >= arr.length) return arr;
  const next = [...arr];
  [next[fromIdx], next[toIdx]] = [next[toIdx], next[fromIdx]];
  return next;
};

export function stepSupportsFields(step: TVerificationAppElement): boolean {
  const cat = STEP_TYPE_CATEGORY[step.type];
  return cat !== undefined && STEP_TYPES_WITH_FIELDS.includes(cat);
}

export function isStaticString(v: unknown): v is string {
  return typeof v === "string";
}

export function isStaticBoolean(v: unknown): v is boolean {
  return typeof v === "boolean";
}

export function isDynamic(v: unknown): boolean {
  return typeof v === "object" && v !== null;
}

export function isDynamicDefault(value: unknown): value is DynamicDefault {
  return (
    typeof value === "object"
    && value !== null
    && !Array.isArray(value)
    && "field" in value
    && typeof (value as { field: unknown }).field === "string"
    && !("op" in value)
  );
}

export function fieldSupportsStaticDefaultEditing(type: TConfigItemType): boolean {
  return FIELD_TYPES_WITH_STATIC_DEFAULT_EDITOR.includes(type);
}

export function getFieldDefaultEditorKind(
  field: TVerificationAppFieldConfigItem,
): FieldDefaultEditorKind {
  if (isDynamicDefault(field.default)) {
    return "dynamic";
  }

  if (field.type === TConfigItemType.PEP) {
    return typeof field.default === "string"
      || typeof field.default === "number"
      || Array.isArray(field.default)
      ? "none"
      : "falseBoolean";
  }

  if (typeof field.default === "string") {
    return "string";
  }

  if (typeof field.default === "number") {
    return "number";
  }

  if (typeof field.default === "boolean") {
    return "boolean";
  }

  if (Array.isArray(field.default)) {
    return getArrayDefaultEditorKind(field, field.default);
  }

  if (field.default !== undefined) {
    return "none";
  }

  return inferFieldDefaultEditorKind(field);
}

export const FIELD_TYPES_WITH_MULTIPLE: TConfigItemType[] = [
  TConfigItemType.SELECT,
  TConfigItemType.FILE_PICKER,
  TConfigItemType.TAX_ID,
];

export const FIELD_TYPES_WITH_STATIC_DEFAULT_EDITOR: TConfigItemType[] = [
  TConfigItemType.TEXT,
  TConfigItemType.URL,
  TConfigItemType.SELECT,
  TConfigItemType.CHECKBOX,
  TConfigItemType.PEP,
  TConfigItemType.COUNTRY_SELECT,
  TConfigItemType.DATE,
  TConfigItemType.SOURCE_OF_ACTIVE,
];

function isSelectOptionObject(
  item: unknown,
): item is { label: string; value: string | number } {
  return (
    typeof item === "object"
    && item !== null
    && "label" in item
    && "value" in item
    && typeof (item as { label: unknown }).label === "string"
  );
}

function selectDefaultUsesNumbers(field: TVerificationAppFieldConfigItem): boolean {
  const data = (field.data ?? []) as unknown[];

  return data.length > 0 && data.every(
    (item: unknown) => isSelectOptionObject(item) && typeof item.value === "number",
  );
}

function getInferredSelectDefaultEditorKind(
  field: TVerificationAppFieldConfigItem,
): FieldDefaultEditorKind {
  const scalarKind: FieldDefaultEditorKind = selectDefaultUsesNumbers(field)
    ? "number"
    : "string";

  if (!field.multiple) {
    return scalarKind;
  }

  return scalarKind === "number" ? "numberArray" : "stringArray";
}

function inferFieldDefaultEditorKind(
  field: TVerificationAppFieldConfigItem,
): FieldDefaultEditorKind {
  switch (field.type) {
    case TConfigItemType.TEXT:
    case TConfigItemType.URL:
    case TConfigItemType.COUNTRY_SELECT:
    case TConfigItemType.DATE:
      return "string";
    case TConfigItemType.CHECKBOX:
      return "boolean";
    case TConfigItemType.PEP:
      return "falseBoolean";
    case TConfigItemType.SOURCE_OF_ACTIVE:
      return "stringArray";
    case TConfigItemType.SELECT:
      return getInferredSelectDefaultEditorKind(field);
    default:
      return "none";
  }
}

function getArrayDefaultEditorKind(
  field: TVerificationAppFieldConfigItem,
  value: unknown[],
): FieldDefaultEditorKind {
  if (value.length > 0 && value.every((item) => typeof item === "number")) {
    return "numberArray";
  }

  if (value.length > 0 && value.every((item) => typeof item === "string")) {
    return "stringArray";
  }

  if (value.length === 0) {
    const inferredKind = inferFieldDefaultEditorKind(field);
    return inferredKind === "number" || inferredKind === "numberArray"
      ? "numberArray"
      : "stringArray";
  }

  return "none";
}

/** Field names are required for value fields and must match snake_case. */
export function isValidFieldName(name: string): boolean {
  return FIELD_NAME_PATTERN.test(name);
}
